// The Living Khipu's cords: fifteen dyed strings simulated as chains of N
// points (Verlet integration), pinned wherever the current scene ties them.
//
// A cord's points live in viewport pixels. Scenes (./scenes.ts) say where
// each cord is pinned and how long it is; `step` moves the points under
// gravity and pushes from the pointer, then relaxes the chain so every link
// keeps its length.
import { Cycle, KnotState } from "../lib/knot";

export const N = 34;

export type Dye =
  | "crimson"
  | "gold"
  | "olive"
  | "rust"
  | "walnut"
  | "fawn"
  | "madder"
  | "natural";

/** The brand crimson first, then pendants in every dye. */
export const DYES: Dye[] = [
  "crimson",
  "gold",
  "olive",
  "rust",
  "walnut",
  "fawn",
  "madder",
  "natural",
  "crimson",
  "gold",
  "olive",
  "rust",
  "walnut",
  "fawn",
  "madder",
];

export type Pin = {
  idx: number;
  x: number;
  y: number;
  /** Where the pin is heading; it eases there over its first moments. */
  tx: number;
  ty: number;
  age: number;
  /** Leave the pin in this direction (radians), for strands that fan out. */
  dir?: number;
};

/** A knot tied on a cord, animating between states (see src/lib/knot.ts). */
export type KnotRun = {
  id: string;
  st: KnotState;
  cyc: Cycle | null;
  t0: number;
  n: number;
  /** Where on the cord, 0..1. */
  s: number;
  /** Scale: `k` if the scene set one, else from the cord's width. */
  k: number;
  kk: number;
  spanPx: number;
  /** Drawn after every cord, over whatever crosses it. */
  top: boolean;
  /** New turns wind on at one end rather than around the middle. */
  grow: boolean;
  dying: boolean;
};

export type Cord = {
  i: number;
  dye: Dye;
  x: Float64Array;
  y: Float64Array;
  px: Float64Array;
  py: Float64Array;
  /** Point masses: heavier points carry what hangs from them. */
  m: Float64Array;
  pin: Uint8Array;
  /** Cumulative length at each point, from measureCord. */
  cum: Float64Array;
  total: number;
  len: number;
  w: number;
  alpha: number;
  stiff: number;
  stiffFrom: number;
  damp: number;
  /** Grows from 0.04 to 1 as the cord drops in on first load. */
  intro: number;
  introAt: number;
  /** Hitched over the cord above: drawn with a short loop at its top. */
  hitch: number;
  ply: number;
  beads: number;
  seg: number;
  /** Its length at the last step (0 before the first), to carry a change. */
  lenWas: number;
  /** How far a point may swing off the line between its pins, px. */
  reach: number;
  /** How hard the air sways it, 1 as the scenes' cords. */
  sway: number;
  /** How heavy it hangs, 1 as the scenes' cords. */
  weight: number;
  pins: Record<string, Pin>;
  knots: Map<string, KnotRun>;
};

export const clamp = (v: number, a: number, b: number) =>
  v < a ? a : v > b ? b : v;
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smooth = (t: number) => t * t * (3 - 2 * t);
export const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
/** A stable pseudo-random number in 0..1 for an index. */
export const hash = (i: number) => {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

export const makeCord = (i: number, dye: Dye): Cord => ({
  i,
  dye,
  x: new Float64Array(N),
  y: new Float64Array(N),
  px: new Float64Array(N),
  py: new Float64Array(N),
  m: new Float64Array(N).fill(1),
  pin: new Uint8Array(N),
  cum: new Float64Array(N),
  total: 1,
  len: 60,
  w: 4,
  alpha: 0,
  stiff: 0,
  stiffFrom: 0,
  damp: 0,
  intro: 1,
  introAt: 0,
  hitch: 0,
  ply: 0,
  beads: 0,
  seg: 2,
  lenWas: 0,
  reach: Infinity,
  sway: 1,
  weight: 1,
  pins: {},
  knots: new Map(),
});

export const measureCord = (c: Cord) => {
  let s = 0;
  c.cum[0] = 0;
  for (let k = 1; k < N; k++) {
    s += Math.hypot(c.x[k] - c.x[k - 1], c.y[k] - c.y[k - 1]);
    c.cum[k] = s;
  }
  c.total = Math.max(s, 1e-3);
};

/** The point `s` (0..1) of the way along the cord, and the link it's on. */
export const pointAt = (c: Cord, s: number) => {
  const d = clamp(s, 0, 1) * c.total;
  let k = 1;
  while (k < N - 1 && c.cum[k] < d) k++;
  const a = c.cum[k - 1];
  const b = c.cum[k];
  const t = b > a ? clamp((d - a) / (b - a), 0, 1) : 0;
  return {
    x: lerp(c.x[k - 1], c.x[k], t),
    y: lerp(c.y[k - 1], c.y[k], t),
    k,
  };
};

/** A point on the cord and the cord's direction there, over `span` px. */
export const frameAt = (c: Cord, s: number, span: number) => {
  const e = span / c.total;
  const p = pointAt(c, s);
  const a = pointAt(c, s - e);
  const b = pointAt(c, s + e);
  return { x: p.x, y: p.y, ang: Math.atan2(b.y - a.y, b.x - a.x), k: p.k };
};

export type Pointer = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  on: boolean;
  touch: boolean;
};

/** A cord point held by the pointer. */
export type Drag = { c: Cord; k: number };

export type Sim = {
  cords: Cord[];
  ptr: Pointer;
  drag: Drag | null;
  /** Phones: lighter gravity and less sway. */
  M: boolean;
  reduce: boolean;
  /** 0 at a scene, 1 halfway between two: cords are damped mid-move. */
  mid: number;
};

const ITER = 12;

const setPins = (sim: Sim, c: Cord) => {
  for (const key in c.pins) {
    const p = c.pins[key];
    c.x[p.idx] = p.x;
    c.y[p.idx] = p.y;
    if (p.dir != null && p.idx + 1 < N) {
      c.x[p.idx + 1] = p.x + Math.cos(p.dir) * c.seg;
      c.y[p.idx + 1] = p.y + Math.sin(p.dir) * c.seg;
    }
  }
  if (sim.drag && sim.drag.c === c) {
    c.x[sim.drag.k] = sim.ptr.x;
    c.y[sim.drag.k] = sim.ptr.y;
  }
};

// Pull each link back to its rest length, sweeping forwards or backwards.
const solve = (c: Cord, rev: boolean) => {
  const { seg, x: X, y: Y, pin: P, m: Ms } = c;
  for (let q = 0; q < N - 1; q++) {
    const k = rev ? N - 2 - q : q;
    const dx = X[k + 1] - X[k];
    const dy = Y[k + 1] - Y[k];
    const d = Math.sqrt(dx * dx + dy * dy) || 1e-6;
    const diff = (d - seg) / d;
    const w1 = P[k] ? 0 : 1 / Ms[k];
    const w2 = P[k + 1] ? 0 : 1 / Ms[k + 1];
    const ws = w1 + w2;
    if (!ws) continue;
    const a = (diff * w1) / ws;
    const b = (diff * w2) / ws;
    X[k] += dx * a;
    Y[k] += dy * a;
    X[k + 1] -= dx * b;
    Y[k + 1] -= dy * b;
  }
  // Stiff strands resist bending for their first few joints.
  for (let k = c.stiffFrom; k < c.stiffFrom + c.stiff && k + 2 < N; k++) {
    const dx = X[k + 2] - X[k];
    const dy = Y[k + 2] - Y[k];
    const d = Math.sqrt(dx * dx + dy * dy) || 1e-6;
    const rest = seg * 1.99;
    if (d >= rest) continue;
    const w1 = P[k] ? 0 : 1;
    const w2 = P[k + 2] ? 0 : 1;
    const ws = w1 + w2;
    if (!ws) continue;
    const diff = ((d - rest) / d) * 0.9;
    X[k] += (dx * diff * w1) / ws;
    Y[k] += (dy * diff * w1) / ws;
    X[k + 2] -= (dx * diff * w2) / ws;
    Y[k + 2] -= (dy * diff * w2) / ws;
  }
  // A knot binds the cord, so keep the stretch under it straight.
  for (const kn of c.knots.values()) {
    if (kn.st.p < 0.4) continue;
    const half = kn.spanPx / 2 / seg;
    const at = kn.s * (N - 1);
    const i0 = clamp(Math.floor(at - half), 0, N - 1);
    const i1 = clamp(Math.ceil(at + half), 0, N - 1);
    if (i1 - i0 < 2) continue;
    const rest = (i1 - i0) * seg;
    const dx = X[i1] - X[i0];
    const dy = Y[i1] - Y[i0];
    const d = Math.sqrt(dx * dx + dy * dy) || 1e-6;
    if (d >= rest * 0.998) continue;
    const w1 = P[i0] ? 0 : 1;
    const w2 = P[i1] ? 0 : 1;
    const ws = w1 + w2;
    if (!ws) continue;
    const diff = ((d - rest) / d) * 0.5;
    X[i0] += (dx * diff * w1) / ws;
    Y[i0] += (dy * diff * w1) / ws;
    X[i1] -= (dx * diff * w2) / ws;
    Y[i1] -= (dy * diff * w2) / ws;
  }
};

// Long-range tethers: no point may sit farther from a pin than the cord
// between them allows. Skipped while two pins are pulled further apart than
// the cord reaches, or the tethers would fight each other.
const tether = (sim: Sim, c: Cord) => {
  const { seg, x: X, y: Y, pin: P } = c;
  const ps: number[] = [];
  for (const key in c.pins) ps.push(c.pins[key].idx);
  if (sim.drag && sim.drag.c === c) ps.push(sim.drag.k);
  ps.sort((a, b) => a - b);
  for (let q = 1; q < ps.length; q++) {
    const a = ps[q - 1];
    const b = ps[q];
    if (Math.hypot(X[b] - X[a], Y[b] - Y[a]) > (b - a) * seg * 1.002) return;
  }
  for (const ip of ps) {
    const px = X[ip];
    const py = Y[ip];
    for (let k = 0; k < N; k++) {
      if (P[k]) continue;
      const max = Math.abs(k - ip) * seg;
      const dx = X[k] - px;
      const dy = Y[k] - py;
      const d2 = dx * dx + dy * dy;
      if (d2 > max * max) {
        const f = max / Math.sqrt(d2);
        X[k] = px + dx * f;
        Y[k] = py + dy * f;
      }
    }
  }
};

// Keep each point within reach of the line between the pins either side of
// it, so a long cord's slack can't carry it out of its column.
const confine = (c: Cord) => {
  if (c.reach === Infinity) return;
  const ps = Object.values(c.pins).sort((a, b) => a.idx - b.idx);
  if (ps.length < 2) return;
  let q = 0;
  for (let k = 0; k < N; k++) {
    while (q < ps.length - 2 && ps[q + 1].idx <= k) q++;
    if (c.pin[k]) continue;
    const a = ps[q];
    const b = ps[q + 1];
    const base = lerp(a.x, b.x, clamp((k - a.idx) / (b.idx - a.idx), 0, 1));
    const x = clamp(c.x[k], base - c.reach, base + c.reach);
    // Against the wall it stops, rather than bouncing off.
    c.px[k] += x - c.x[k];
    c.x[k] = x;
  }
};

// The pointer brushes cords aside as it passes.
const push = (sim: Sim, c: Cord) => {
  const { ptr } = sim;
  const R = ptr.touch ? 46 : 70;
  const R2 = R * R;
  for (let k = 0; k < N; k++) {
    if (c.pin[k]) continue;
    const dx = c.x[k] - ptr.x;
    const dy = c.y[k] - ptr.y;
    const d2 = dx * dx + dy * dy;
    if (d2 >= R2 || d2 < 0.01) continue;
    const d = Math.sqrt(d2);
    const f = 1 - d / R;
    c.x[k] += (dx / d) * f * 1.5 + ptr.vx * f * 0.16;
    c.y[k] += (dy / d) * f * 1.5 + ptr.vy * f * 0.16;
  }
};

// Pins moving slower than this (px/s) leave their cords to swing after them
// (a cord they hang from swaying); from there to CARRY_TO, more and more of
// the move is carried (the page scrolling under them, a scene changing).
const CARRY_FROM = 600;
const CARRY_TO = 1800;

// Carry a cord's free points along with its pins' quick moves (each point
// by the pins either side of it), and draw them in towards the pins or let
// them out as the cord's length changed (`grew`, a ratio). Their last
// positions move with them, so they keep the speed they had: a quick scroll
// can't swing the cords round like whips, nor reeling one in fling its end.
const carryCord = (
  c: Cord,
  ps: Pin[],
  was: number[][],
  grew: number,
  dt: number,
) => {
  let still = grew === 1;
  const moves = ps.map((p, q) => {
    const dx = p.x - was[q][0];
    const dy = p.y - was[q][1];
    const v = Math.hypot(dx, dy) / dt;
    const f = smooth(clamp((v - CARRY_FROM) / (CARRY_TO - CARRY_FROM), 0, 1));
    if (f > 0) still = false;
    return [dx * f, dy * f];
  });
  if (still) return;
  // The pins in order along the cord, and the first at or past point k.
  const order = ps.map((_, q) => q).sort((p, q) => ps[p].idx - ps[q].idx);
  let at = 0;
  for (let k = 0; k < N; k++) {
    while (at < order.length && ps[order[at]].idx < k) at++;
    if (c.pin[k]) continue;
    const a = at > 0 ? order[at - 1] : -1;
    const b = at < order.length ? order[at] : -1;
    // What the point hangs from: between two pins, the point on the line
    // joining them, else the one pin on its side.
    const t = a < 0 ? 1 : b < 0 ? 0 : (k - ps[a].idx) / (ps[b].idx - ps[a].idx);
    const u = a < 0 ? b : a;
    const v = b < 0 ? a : b;
    const x0 = lerp(was[u][0], was[v][0], t);
    const y0 = lerp(was[u][1], was[v][1], t);
    const dx = lerp(moves[u][0], moves[v][0], t) + (c.x[k] - x0) * (grew - 1);
    const dy = lerp(moves[u][1], moves[v][1], t) + (c.y[k] - y0) * (grew - 1);
    c.x[k] += dx;
    c.y[k] += dy;
    c.px[k] += dx;
    c.py[k] += dy;
  }
};

/** Advance every cord by `dt` seconds; `time` drives the idle sway. */
export const step = (sim: Sim, dt: number, time: number) => {
  const { cords, reduce, M, mid } = sim;
  const g = (M ? 1250 : 1500) * dt * dt;
  const damp = reduce ? 0.9 : 0.978;
  const sway = reduce ? 0 : M ? 0.012 : 0.02;
  for (const c of cords) {
    c.seg = Math.max(0.4, (c.len * c.intro) / (N - 1));
    c.pin.fill(0);
    const ps = Object.values(c.pins);
    const was = ps.map((p) => [p.x, p.y]);
    for (const p of ps) {
      p.age += dt;
      const k = p.age > 0.45 || reduce ? 1 : 1 - Math.exp(-dt * 10);
      p.x += (p.tx - p.x) * k;
      p.y += (p.ty - p.y) * k;
      c.pin[p.idx] = 1;
      if (p.dir != null && p.idx + 1 < N) c.pin[p.idx + 1] = 1;
    }
    const grew = c.lenWas > 0 ? c.len / c.lenWas : 1;
    c.lenWas = c.len;
    if (ps.length) carryCord(c, ps, was, grew, dt);
    if (sim.drag && sim.drag.c === c) c.pin[sim.drag.k] = 1;
    const dm = lerp(c.damp && !reduce ? c.damp : damp, 0.95, mid);
    for (let k = 0; k < N; k++) {
      if (c.pin[k]) {
        c.px[k] = c.x[k];
        c.py[k] = c.y[k];
        continue;
      }
      let vx = (c.x[k] - c.px[k]) * dm;
      let vy = (c.y[k] - c.py[k]) * dm;
      const sp = vx * vx + vy * vy;
      if (sp > 900) {
        const f = 30 / Math.sqrt(sp);
        vx *= f;
        vy *= f;
      }
      c.px[k] = c.x[k];
      c.py[k] = c.y[k];
      c.x[k] +=
        vx +
        sway *
          c.sway *
          Math.sin(time * 0.9 + c.i * 1.93 + k * 0.23) *
          (k / (N - 1));
      c.y[k] += vy + g * c.weight;
    }
    if (sim.ptr.on && !reduce && !sim.drag && c.alpha > 0.3) push(sim, c);
  }
  for (let it = 0; it < ITER; it++) {
    for (const c of cords) {
      setPins(sim, c);
      solve(c, (it & 1) === 1);
      tether(sim, c);
      confine(c);
    }
  }
  for (const c of cords) setPins(sim, c);
};

/**
 * Lay a cord straight between its pins, at rest, so it starts where the
 * scene wants it rather than falling in from nowhere. Unpinned stretches
 * hang from the nearest pin.
 */
export const layCord = (c: Cord, W: number) => {
  const seg = Math.max(0.4, (c.len * c.intro) / (N - 1));
  const ps = Object.values(c.pins).sort((a, b) => a.idx - b.idx);
  for (const p of ps) {
    p.x = p.tx;
    p.y = p.ty;
    p.age = 1;
  }
  for (let k = 0; k < N; k++) {
    let x = W / 2;
    let y = -200 + k * seg;
    const before = ps.filter((p) => p.idx <= k).pop();
    const after = ps.find((p) => p.idx >= k);
    if (before && after && before !== after) {
      const t = (k - before.idx) / (after.idx - before.idx);
      x = lerp(before.x, after.x, t);
      y = lerp(before.y, after.y, t);
    } else if (before) {
      const dir = before.dir ?? Math.PI / 2;
      x = before.x + Math.cos(dir) * (k - before.idx) * seg;
      y = before.y + Math.sin(dir) * (k - before.idx) * seg;
    } else if (after) {
      x = after.x;
      y = after.y - (after.idx - k) * seg;
    }
    c.x[k] = c.px[k] = x;
    c.y[k] = c.py[k] = y;
  }
};

/** The cord point nearest (x, y) within `r` px, if it isn't pinned. */
export const nearest = (cords: Cord[], x: number, y: number, r: number) => {
  let best: Drag | null = null;
  let bd = r;
  for (const c of cords) {
    if (c.alpha < 0.5 || c.ply > 0.5) continue;
    for (let k = 0; k < N - 1; k++) {
      const ax = c.x[k];
      const ay = c.y[k];
      const dx = c.x[k + 1] - ax;
      const dy = c.y[k + 1] - ay;
      const l2 = dx * dx + dy * dy || 1;
      const t = clamp(((x - ax) * dx + (y - ay) * dy) / l2, 0, 1);
      const d = Math.hypot(ax + dx * t - x, ay + dy * t - y) - c.w / 2;
      if (d < bd) {
        const kk = t < 0.5 ? k : k + 1;
        if (!c.pin[kk]) {
          bd = d;
          best = { c, k: kk };
        }
      }
    }
  }
  return best;
};
